import { useState } from 'react';
import { Medal, Navigation2, PiggyBank, Share2, Trophy, type LucideIcon } from 'lucide-react';
import { useT } from '../hooks/useT';
import type { TranslationKey } from '../utils/i18n';
import { Legenda } from './Legenda';
import { Modal } from './Modal';

interface WelkomModalProps {
  /** Aantal musea op de kaart (als de lijst al geladen is). */
  aantal: number | null;
  onClose: () => void;
}

const EXTRA: { Icon: LucideIcon; key: TranslationKey }[] = [
  { Icon: Navigation2, key: 'welkomExtraBuurt' },
  { Icon: Trophy, key: 'welkomExtraVoortgang' },
  { Icon: Medal, key: 'welkomExtraBadges' },
  { Icon: PiggyBank, key: 'welkomExtraTerug' },
  { Icon: Share2, key: 'welkomExtraDelen' },
];

const STAPPEN = 3;

/**
 * Uitleg bij het eerste gebruik (later terug te vinden in het menu). Terwijl je leest, wordt
 * de kaart op de achtergrond gedownload; hij wordt pas opgebouwd als je op "Start" tikt.
 */
export function WelkomModal({ aantal, onClose }: WelkomModalProps) {
  const { t, tf } = useT();
  const [stap, setStap] = useState(0);
  const laatste = stap === STAPPEN - 1;
  const volgende = () => (laatste ? onClose() : setStap((s) => s + 1));
  const titels: TranslationKey[] = ['welkomTitel1', 'welkomTitel2', 'welkomTitel3'];

  return (
    <Modal title={t(titels[stap])} onClose={onClose} onConfirm={volgende}>
      <div className="min-h-64 space-y-4">
        {stap === 0 && (
          <>
            <p className="leading-relaxed">
              {aantal ? tf('welkomTekst1', { n: aantal }) : t('welkomTekst1Zonder')}
            </p>
            <p className="leading-relaxed text-slate-600 dark:text-slate-400">{t('welkomTekst1b')}</p>
          </>
        )}
        {stap === 1 && (
          <>
            <p className="leading-relaxed">{t('welkomTekst2')}</p>
            <Legenda />
          </>
        )}
        {stap === 2 && (
          <>
            <ul className="space-y-3">
              {EXTRA.map(({ Icon, key }) => (
                <li key={key} className="flex items-start gap-3">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-indigo-100 text-indigo-900 dark:bg-indigo-950/70 dark:text-indigo-200">
                    <Icon size={18} aria-hidden />
                  </span>
                  <span className="pt-1.5 text-sm leading-snug">{t(key)}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-slate-600 dark:text-slate-400">{t('welkomPrivacy')}</p>
          </>
        )}
      </div>

      <div className="mt-5 flex items-center justify-center gap-2" aria-label={tf('welkomStap', { n: stap + 1, totaal: STAPPEN })} role="img">
        {Array.from({ length: STAPPEN }, (_, i) => (
          <span
            key={i}
            aria-hidden
            className={`h-2 rounded-full transition-all duration-200 ${i === stap ? 'w-6 bg-indigo-700 dark:bg-indigo-400' : 'w-2 bg-black/15 dark:bg-white/20'}`}
          />
        ))}
      </div>

      <div className="mt-5 flex gap-3">
        <button
          onClick={stap === 0 ? onClose : () => setStap((s) => s - 1)}
          className="min-h-12 flex-1 whitespace-nowrap rounded-2xl bg-black/5 font-semibold transition-all duration-150 active:scale-95 dark:bg-white/10"
        >
          {stap === 0 ? t('welkomOverslaan') : t('welkomVorige')}
        </button>
        <button
          onClick={volgende}
          autoFocus
          className="min-h-12 flex-1 whitespace-nowrap rounded-2xl bg-indigo-700 font-semibold text-white transition-all duration-150 active:scale-95"
        >
          {laatste ? t('welkomStart') : t('welkomVolgende')}
        </button>
      </div>
    </Modal>
  );
}
